import { useEffect, useRef } from 'preact/hooks';

import { useSelector } from '@/redux/helper';
import { trackMP } from '@/shared/mp';

/**
 * Reports where visitors get to in the flow, and how they leave it.
 *
 * The modals this replaced each fired a `page-view` when they opened, which was enough when a modal
 * was one screen. A flow is several, and the useful question is where people stop — so every step
 * is reported as it is reached, tagged with the flow it belongs to and the gate that started it.
 *
 * Leaving is reported separately, because "finished" and "gave up" are the same event to a step
 * counter and completely different to anyone reading the numbers. `paymentUnconfirmed` is reported
 * too: it means someone paid and we could not see it yet, which is worth knowing about promptly.
 */
const useSpeedyFlowAnalytics = () => {
  const flowState = useSelector((state) => state.modals.speedyFlow);
  const { visible, machine, gate, utm, ppt, habitatId, primaryProductType, secondaryProductType } = flowState;

  const lastStepRef = useRef<string | null>(null);
  const lastUnconfirmedRef = useRef(false);
  /** Kept so the closing event can report what was reached, after the state has been cleared. */
  const lastMachineRef = useRef<typeof machine>(undefined);

  /**
   * The campaign parameters are reported raw rather than as a derived label.
   *
   * They used to be summarised into a single `arrival` name, which meant the funnel could tell a
   * partner widget from a QR code but carried no `utm_*` field at all — so any question the ten
   * names did not anticipate could not be asked of the data at all. Reporting the parameters
   * themselves is strictly more information, and the label can still be derived downstream.
   */
  const common = {
    flow: machine?.flow,
    entryPoint: gate?.entryPoint,
    gateKind: gate?.kind,
    utmSource: utm?.source,
    utmMedium: utm?.medium,
    utmCampaign: utm?.campaign,
    ppt,
    habitatId,
    primaryProductType,
    secondaryProductType,
  };

  useEffect(() => {
    if (machine) {
      lastMachineRef.current = machine;
    }

    if (!visible || !machine) {
      // The flow just closed. Report how it ended, using the state captured before it cleared.
      if (lastStepRef.current) {
        const ended = lastMachineRef.current;
        trackMP('speedy-flow-closed', {
          ...common,
          flow: ended?.flow,
          lastStep: lastStepRef.current,
          outcome: ended?.purchased ? 'purchased' : ended?.hasAccount ? 'signed-in' : 'dismissed',
          hasPersona: ended?.hasPersona,
        });
        lastStepRef.current = null;
        lastUnconfirmedRef.current = false;
      }
      return;
    }

    if (machine.step !== lastStepRef.current) {
      lastStepRef.current = machine.step;
      trackMP('speedy-flow-step', { ...common, step: machine.step, variant: machine.completionVariant });
    }

    // Payment taken but the entitlement has not appeared within budget. Not an error for the
    // visitor, but the one number worth watching if webhook latency drifts.
    if (machine.unconfirmed && !lastUnconfirmedRef.current) {
      lastUnconfirmedRef.current = true;
      trackMP('speedy-flow-payment-unconfirmed', { ...common });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, machine?.step, machine?.unconfirmed]);
};

export default useSpeedyFlowAnalytics;
